/**
 * Landing hero layout — widths, breakpoints, and viewport-relative spacing.
 * Consumed via `theme.landingHeroLayout` (see module augmentation below + `theme.ts`).
 * Page JSON (`heroSpacing`) overrides numeric spacing keys only; these objects are the defaults.
 */
export const landingHeroLayoutTokens = {
    hero: {
        /**
         * Content-height only — no `100svh` floor + no extra bottom pad, so the photo layer does not
         * extend past the first screen just to satisfy a min-height (that read as “bg below the monitor”).
         */
        minHeight: 'min-content',
        paddingTop: { xs: 0.75, sm: 1, md: 1.25 },
        /** Trust straddle uses overlap `mb`; extra bottom pad here was stretching the hero photo band */
        paddingBottomWithTrustFeatures: {
            xs: 0,
            md: 0,
            lg: 0,
        },
        paddingBottomNoTrustFeatures: { xs: 1.75, md: 2, lg: 1.85 },
        /** `rem` so it tracks root like the rest of the hero (avoids px-only hero chrome). */
        bottomFadeHeight: { xs: '4.5rem', sm: '5.5rem', md: '6rem' },
        /** Taller photo band on wide viewports (bg layer is `absolute` inset — follows this min height). */
        wideShellSx: {
            '@media (min-width: 1760px)': {
                minHeight: 'clamp(40rem, min(58vh, 62rem), 76rem)',
            },
        },
    },
    /** Hero text under title / image — `rem` + breakpoints only (no `vmin` / `svh`). */
    heroCopy: {
        subheading: {
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            lineHeight: 1.25,
            fontSize: {
                xs: '0.6875rem',
                sm: '0.75rem',
                md: '0.8125rem',
                lg: '0.8125rem',
            },
        },
        description: {
            fontWeight: 400,
            lineHeight: 1.55,
            fontSize: {
                xs: '0.875rem',
                sm: '0.9375rem',
                md: '1rem',
                lg: '1.0625rem',
            },
        },
        /** Wider hero lead on `xl` so title block + search aren’t separated by a huge empty band. */
        copyColumnMaxWidth: {
            xs: '100%',
            lg: 'min(100%, 620px)',
            xl: 'min(100%, 760px)',
        },
    },
    headlineRow: {
        gap: { xs: 1.25, md: 1.5, lg: 2, xl: 2.5 },
        marginBottom: { xs: 0.75, md: 1 },
    },
    titleImage: {
        maxWidth: {
            xs: 'min(100%, 24rem)',
            sm: 'min(100%, 27.5rem)',
            md: 'min(100%, 29rem)',
            lg: 'min(100%, 31rem)',
            xl: 'min(100%, 34rem)',
        },
        maxHeight: {
            xs: '3.25rem',
            sm: '4rem',
            md: '4.5rem',
            lg: '4.75rem',
            xl: '5.25rem',
        },
    },
    /** Applied on hero title `<img>` with `width` / `maxHeight` from `titleImage` (wide = larger mark + ~1905). */
    titleImageWideSx: {
        '@media (min-width: 1760px)': {
            width: 'min(100%, 38rem)',
            maxHeight: '6.25rem',
        },
    },
    searchForm: {
        flex: { lg: '0 1 min(100%, 680px)', xl: '0 1 min(100%, 780px)' },
        width: { xs: '100%', lg: 'auto' },
        maxWidth: { lg: 680, xl: 780 },
        searchIconFontSize: { xs: 20, sm: 22, md: 24, lg: 26 },
        inputPadding: {
            pl: { xs: 1.15, sm: 1.35, md: 1.5 },
            pr: { xs: 1.25, sm: 1.75, md: 2 },
            py: { xs: 0.85, sm: 1, md: 1.05 },
        },
        submitButton: {
            px: { xs: 2, sm: 3, md: 3.5 },
            minWidth: { xs: '5.5rem', sm: '6.75rem', md: '7.25rem' },
        },
    },
    agencies: {
        marginTop: { xs: 1.55, md: 2.15 },
        marginBottomDefault: { xs: 3, md: 3.5 },
        panelPaddingTop: { xs: 1, md: 1.15 },
        sectionPadding: {
            pt: { xs: 1.15, md: 1.45 },
            pb: { xs: 0.5, md: 0.75 },
        },
    },
    agencyCarousel: {
        section: {
            width: '100%',
            maxWidth: '100%',
            px: 0,
            boxSizing: 'border-box',
        },
        chrome: {
            borderRadius: '20px',
            width: '100%',
        },
        list: {
            scrollPaddingLeft: {
                xs: 2,
                sm: 2.5,
                md: 3,
                lg: 3.5,
                xl: 4,
            },
            scrollPaddingRight: {
                xs: 2,
                sm: 2.5,
                md: 3,
                lg: 3.5,
                xl: 4,
            },
            /** `rem` + breakpoints only — avoid `vmin` here (inverts vs display % when height dominates). */
            gap: { xs: '0.75rem', sm: '0.875rem', md: '1rem', lg: '1.125rem' },
            py: 0.5,
            /** Slightly roomier horizontal inset so five-across tiles don’t feel glued to the chrome edge. */
            px: { xs: 0.5, md: 0.85, lg: 1.15, xl: 1.35 },
        },
        pager: {
            marginTop: { xs: 0.85, md: 0.95 },
            gap: { xs: 0.75, sm: 1 },
            dotNavGap: { xs: 0.55, sm: 0.65 },
            iconButtonPx: { xs: 28, sm: 30, md: 32 },
            chevronIconSize: { xs: 14, sm: 15, md: 16 },
            dotHitPx: { xs: 28, sm: 30 },
            dotMinHeightPx: { xs: 28, sm: 30 },
            dotInactiveWidthPx: 8,
            dotActiveWidthPx: 10,
            dotHeightPx: 8,
            /**
             * Reserved strip height under the cards so the trust band does not jump when skeleton → data,
             * or when pager controls fade in (single-page vs multi-page uses the same vertical slot).
             */
            rowReservedMinPx: { xs: 30, sm: 32, md: 34 },
        },
    },
    agencyTile: {
        /**
         * **Same width for every tile** at a given breakpoint. From `md` up,
         * width tracks row targets for denser grid display: **6 / 7 / 8 cards** per row.
         * (`gap` must stay in sync with `agencyCarousel.list.gap`).
         */
        column: {
            flex: '0 0 auto',
            boxSizing: 'border-box',
            width: {
                xs: 'clamp(11.25rem, 88vw, 13.75rem)',
                /** Four across — readable on tablet / small desktop widths. */
                sm: 'max(8.75rem, calc((100% - 3 * 0.875rem) / 4))',
                /** Seven across — `md` gap is 1rem, so 6 gutters. */
                md: 'calc((100% - 6 * 1rem) / 7)',
                /** Eight across — `lg` gap is 1.125rem, so 7 gutters. */
                lg: 'calc((100% - 7 * 1.125rem) / 8)',
                /** Eight across on xl as well, but with a smaller card footprint. */
                xl: 'calc((100% - 7 * 1.125rem) / 8)',
            },
            minWidth: {
                xs: 'clamp(11.25rem, 88vw, 13.75rem)',
                sm: 'max(8.75rem, calc((100% - 3 * 0.875rem) / 4))',
                md: 'calc((100% - 6 * 1rem) / 7)',
                lg: 'calc((100% - 7 * 1.125rem) / 8)',
                xl: 'calc((100% - 7 * 1.125rem) / 8)',
            },
            maxWidth: {
                xs: 'clamp(11.25rem, 88vw, 13.75rem)',
                sm: 'max(8.75rem, calc((100% - 3 * 0.875rem) / 4))',
                md: 'calc((100% - 6 * 1rem) / 7)',
                lg: 'calc((100% - 7 * 1.125rem) / 8)',
                xl: 'calc((100% - 7 * 1.125rem) / 8)',
            },
            display: 'flex',
            alignItems: 'stretch',
            scrollSnapAlign: 'start',
        },
        portraitMinHeight: {
            xs: '16.75rem',
            sm: '17.75rem',
            md: '18.25rem',
            lg: '20rem',
        },
        portrait: {
            flex: 1,
            alignSelf: 'stretch',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textAlign: 'center',
            borderRadius: '17px',
            p: { xs: 1.75, md: 1.85, lg: 1.95 },
            pt: { xs: 1.85, md: 1.95 },
            pb: { xs: 1.75, md: 1.85 },
            gap: { xs: 1.35, md: 1.5 },
            border: 'none',
            boxShadow: 'none',
            overflow: 'visible',
            transition: 'transform 0.52s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.35s ease, box-shadow 0.35s ease',
            position: 'relative',
            zIndex: 0,
        },
        /** Grouped-agency count chip — label scales with breakpoint `rem`. */
        groupedBadgeChipLabelSx: {
            '& .MuiChip-label': {
                px: 0.75,
                fontWeight: 800,
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.72rem', lg: '0.75rem' },
                '@media (min-width: 1760px)': {
                    fontSize: '0.65rem',
                },
            },
        },
        logoArea: {
            width: '100%',
            flex: '1 1 auto',
            minHeight: { xs: '7.15rem', md: '7.75rem', lg: '8.25rem' },
            maxHeight: { xs: '9rem', md: '9.5rem', lg: '10rem' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '14px',
            bgcolor: 'transparent',
            border: 'none',
            transformOrigin: 'center center',
        },
        logoInner: {
            width: '90%',
            height: '90%',
            maxHeight: { xs: '9rem', md: '9.5rem', lg: '10rem' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        logoImg: {
            width: '100%',
            height: '100%',
            maxHeight: { xs: '8.5rem', md: '9rem', lg: '9.5rem' },
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
        },
        decorIcon: {
            fontSize: { xs: '2.6rem', sm: '2.75rem', md: '2.9rem', lg: '3rem', xl: '3.1rem' },
        },
        /** Fixed vertical slot so every tile dedicates the same space to the 2-line title. */
        titleBadgeWrapper: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '2.6em',
            maxHeight: '2.6em',
        },
        titleTypography: {
            fontWeight: 700,
            /** Slightly denser line box so two lines hold more characters before clamp. */
            lineHeight: 1.26,
            letterSpacing: '-0.015em',
            /** Scaled for wider five-across tiles — prioritize legible full words in two lines. */
            fontSize: {
                xs: '0.75rem',
                sm: '0.8125rem',
                md: '0.8125rem',
                lg: '0.875rem',
                xl: '0.9375rem',
            },
            '@media (min-width: 1760px)': {
                fontSize: '0.8125rem',
            },
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            /** Prefer ending on a clean line break; very long tokens still wrap via overflow-wrap. */
            textOverflow: 'clip',
            overflowWrap: 'break-word',
            wordBreak: 'normal',
            hyphens: 'auto',
            /** Keep single-line titles vertically centered in the 2-line slot */
            alignSelf: 'center',
            width: '100%',
            textAlign: 'center',
        },
        captionTypography: {
            lineHeight: 1.36,
            fontSize: {
                xs: '0.625rem',
                sm: '0.6875rem',
                md: '0.75rem',
                lg: '0.8125rem',
            },
            '@media (min-width: 1760px)': {
                fontSize: '0.625rem',
            },
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            overflowWrap: 'anywhere',
        },
    },
    /** Narrow cap + viewport padding so hover cards stay on-screen; Popper preventOverflow does the rest */
    agencyTooltip: {
        maxWidth: 'min(420px, calc(100vw - 24px))',
    },
    trustBar: {
        /** `rem` + spacing — no `svh` (same reasons as hero: predictable vs display %). */
        marginTopDefault: {
            /** ~1px tighter vs prior defaults — trust band sits slightly higher under the carousel. */
            xs: '0.6875rem',
            sm: '0.8125rem',
            md: '0.9375rem',
            lg: '1.0625rem',
        },
        marginBottom: { xs: '0rem', sm: '0rem', md: '0rem' },
        seamNudgeTransform: {
            xs: 'translateY(0rem)',
            sm: 'translateY(0rem)',
            md: 'translateY(0rem)',
        },
        card: {
            borderRadius: '14px',
            py: { xs: 1.65, md: 2.1 },
            px: { xs: 2.35, md: 3 },
            gap: { xs: 2.25, md: 2.75 },
            border: '1px solid',
            boxShadow: '0 6px 16px rgba(12, 38, 70, 0.1)',
        },
        /**
         * Column 1: icon spans full text height and is vertically centered (balanced vs title + desc).
         * Column 2: title row 1, description row 2.
         */
        featureRow: {
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr)',
            gridTemplateRows: 'auto auto',
            columnGap: 1.5,
            rowGap: 0.35,
            px: { xs: 0.35, md: 0.55 },
            minWidth: 0,
            width: '100%',
            justifySelf: 'stretch',
            alignItems: 'start',
        },
        featureIcon: {
            fontSize: { xs: 30, sm: 32, lg: 36, xl: 40 },
            flexShrink: 0,
            gridColumn: 1,
            gridRow: '1 / -1',
            alignSelf: 'center',
            justifySelf: 'start',
        },
        featureTitle: {
            fontWeight: 700,
            fontSize: {
                xs: '0.9375rem',
                sm: '1rem',
                md: '1.0625rem',
                lg: '1.125rem',
            },
        },
        featureSubtitle: {
            lineHeight: 1.45,
            fontSize: {
                xs: '0.8125rem',
                md: '0.875rem',
                lg: '0.9375rem',
            },
        },
        gridTemplateColumns: {
            three: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            four: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        },
    },
    departmentsBand: {
        paddingTopWithTrustFeatures: {
            xs: '1.5rem',
            sm: '1.75rem',
            md: '2rem',
            lg: '2.25rem',
            xl: '2.5rem',
        },
        /** Default row gap when JSON `heroSpacing.departmentGridGap` is omitted */
        listGap: { xs: 2, sm: 2.5, md: 3 },
        headingBlock: {
            marginTopWithTrust: { xs: 0.5, md: 0.75 },
            marginTopNoTrust: 0,
        },
        tileMinHeights: { xs: 150, sm: 160, md: 172, lg: 180, xl: 186 },
        tileIconSizes: { xs: 28, sm: 29, md: 30, lg: 32, xl: 34 },
        tileBadgeWidthPct: '62%',
        subheading: {
            fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem', lg: '1.125rem' },
            lineHeight: 1.65,
        },
        tileBadgeBox: {
            width: { xs: 52, sm: 54, md: 56, lg: 58 },
            height: { xs: 52, sm: 54, md: 56, lg: 58 },
        },
        tileTitleFontSize: {
            xs: '0.8125rem',
            sm: '0.875rem',
            md: '0.9375rem',
            lg: '1rem',
        },
    },
};
